/** @jsx jsx */
import { Button, CardHorizontalDivider, Container, Flex, Grid, Heading, LaunchIcon, Paragraph } from "@eworkit/iris";
import { isEmpty } from "lodash-es";
import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { PreviewFileEntry } from "@/components/Files/preview/PreviewFileEntry";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { formatFullName } from "@/data/persons";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useFormats } from "@/hooks/Formats";
import { SingleAnswer } from "@/views/consultant-profiles/details/questionnaire/SingleAnswer";
import { SingleWorkStyle } from "@/views/consultant-profiles/details/work-styles/SingleWorkStyle";
import { WORKSTYLES_ICON_MAP } from "@/views/ConsultantProfiles/Detail/WorkStylesSection";
import { JobApplicationPaymentDetailsSummary } from "@/views/job-applications/_components/JobApplicationPaymentDetailsSummary";
import { JobApplicationDetailsContacts } from "@/views/job-applications/details/_components/JobApplicationDetailsContacts";
import { JobApplicationDetailsDecision } from "@/views/job-applications/details/_components/JobApplicationDetailsDecision";
import { JobApplicationDetailsGeneralInformation } from "@/views/job-applications/details/_components/JobApplicationDetailsGeneralInformation";
import { router } from "@/views/router";

interface JobApplicationDetailsDetailsTabProps {
  jobApplication: JobApplication;
}

export const JobApplicationDetailsDetailsTab: FC<JobApplicationDetailsDetailsTabProps> = ({ jobApplication }) => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();
  const { formatDate } = useFormats();

  const { hasCompanyManagerPrivileges } = useContext(UserContext);
  const User = useContext(UserContext);

  const isManager = hasCompanyManagerPrivileges();

  const isConsultantCompanyContext = !isEmpty(User) && User.hasUserRoleInContextCompany("CONSULTANT");

  return (
    <Container>
      <Grid columns={["1fr", null, null, "1fr 2fr"]} gap={["xs", null, null, "sm"]} sx={{ alignItems: "start" }}>
        <Grid gap={["xs", null, null, "sm"]}>
          <JobApplicationDetailsGeneralInformation jobApplication={jobApplication} />
          <JobApplicationDetailsContacts jobApplication={jobApplication} />
        </Grid>

        <Flex flexDirection="column" gap="sm" sx={{ maxWidth: "100vw" }}>
          {jobApplication.comment?.externalComment && (
            <ResponsiveCard sx={{ p: ["sm", null, null, "md"] }}>
              <JobApplicationDetailsDecision jobApplication={jobApplication} />
            </ResponsiveCard>
          )}

          <ResponsiveCard sx={{ p: ["sm", null, null, "md"] }}>
            <Flex alignItems="center" justifyContent="space-between">
              <Flex alignItems="center" gap="xs">
                <LazyUserAvatar user={jobApplication.consultantProfile} />
                <Heading data-test="user-fullName" size="h-sm">
                  {formatFullName(jobApplication.consultantProfile)}
                </Heading>
              </Flex>

              {jobApplication.consultantProfile.parent && isManager && (
                <Button
                  as={Link}
                  to={
                    router.get("consultant-profiles.detail", {
                      consultantProfileId: jobApplication.consultantProfile.parent.id,
                    }).path
                  }
                  target="_blank"
                  display="inline-block"
                  iconRight={<LaunchIcon />}
                  size="sm"
                  variant="secondary">
                  {t("JOB_APPLICATIONS.DETAIL.VIEW_PROFILE")}
                </Button>
              )}
            </Flex>

            <Flex flexDirection="column" py="md" gap="sm">
              <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
                <Grid>
                  <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.NOTICE_PERIOD")}</Paragraph>
                </Grid>
                <Grid>
                  {jobApplication.consultantProfile.availability && (
                    <Paragraph data-test="availability">
                      {jobApplication.consultantProfile.availabilityDate
                        ? formatDate(jobApplication.consultantProfile.availabilityDate)
                        : tCodelist("availability", jobApplication.consultantProfile.availability)}
                    </Paragraph>
                  )}
                </Grid>
              </Grid>
              <Grid columns={["1fr", null, "1fr 3fr"]} gap={["xxs", null, "sm"]}>
                <Grid>
                  <Paragraph color="n700">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.MOTIVATION")}</Paragraph>
                </Grid>
                <Grid>
                  <Paragraph data-test="motivation">
                    {jobApplication.motivation || t("JOB_APPLICATIONS.DETAIL.EMPTY")}
                  </Paragraph>
                </Grid>
              </Grid>
            </Flex>

            {!isConsultantCompanyContext && jobApplication.rate && (
              <React.Fragment>
                <CardHorizontalDivider sx={{ my: 0 }} size="wide" />
                <JobApplicationPaymentDetailsSummary
                  jobRequest={jobApplication.jobRequest}
                  jobApplication={jobApplication}
                />
              </React.Fragment>
            )}

            {jobApplication.answers && jobApplication.answers.length > 0 && (
              <React.Fragment>
                <CardHorizontalDivider sx={{ my: 0 }} size="wide" />
                <Flex data-test="questionnaire" flexDirection="column" gap="sm" py="md">
                  <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.QUESTIONNAIRE.SECTION_HEADER")}</Heading>
                  {jobApplication.answers.map((answer, index) => (
                    <SingleAnswer key={index} answer={answer} />
                  ))}
                </Flex>
              </React.Fragment>
            )}

            {jobApplication.workStyles && jobApplication.workStyles.length > 0 && (
              <React.Fragment>
                <CardHorizontalDivider sx={{ my: 0 }} size="wide" />
                <Flex flexDirection="column" gap="sm" py="md">
                  <Heading size="h-sm">{t("CONSULTANT_PROFILES.DETAIL.WORK_STYLES.SECTION_HEADER")}</Heading>

                  <Flex flexDirection="column" gap="xs">
                    {jobApplication.workStyles
                      ?.sort((a, b) => b.score - a.score)
                      .map(workStyle => (
                        <SingleWorkStyle
                          key={workStyle.type}
                          WorkStyleIcon={WORKSTYLES_ICON_MAP[workStyle.type]}
                          {...workStyle}
                        />
                      ))}
                  </Flex>
                </Flex>
              </React.Fragment>
            )}

            {jobApplication.consultantProfile.cvs && jobApplication.consultantProfile.cvs.length > 0 && (
              <React.Fragment>
                <CardHorizontalDivider sx={{ my: 0 }} size="wide" />
                <Flex flexDirection="column" gap="sm" py="md">
                  <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.CV")}</Heading>
                  <Grid columns={["1fr", null, null, "1fr 1fr"]}>
                    {jobApplication.consultantProfile.cvs.map(cv => (
                      <Grid key={cv.systemId}>
                        <PreviewFileEntry attachment={cv}>{cv.name}</PreviewFileEntry>
                      </Grid>
                    ))}
                  </Grid>
                </Flex>
              </React.Fragment>
            )}

            {jobApplication.attachments && jobApplication.attachments.length > 0 && (
              <React.Fragment>
                <CardHorizontalDivider sx={{ my: 0 }} size="wide" />
                <Flex flexDirection="column" gap="sm" py="md">
                  <Heading size="h-sm">{t("JOB_APPLICATIONS.DETAIL.SECTION_DETAILS.ATTACHMENTS")}</Heading>
                  <Grid columns={["1fr", null, null, "1fr 1fr"]}>
                    {jobApplication.attachments.map(attachment => (
                      <Grid key={attachment.systemId}>
                        <PreviewFileEntry attachment={attachment}>{attachment.name}</PreviewFileEntry>
                      </Grid>
                    ))}
                  </Grid>
                </Flex>
              </React.Fragment>
            )}
          </ResponsiveCard>
        </Flex>
      </Grid>
    </Container>
  );
};
